import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { UserApi } from '../api/user.api';
import { UserCompanyApi } from '../api/user-company.api';
import { UserMultiBuyerEntityApi } from '../api/user-multi-buyer-entity.api';
import { UserProjectManagerApi } from '../api/user-project-manager.api';
import {
  ManagedUser,
  ManagedUsersPage,
  ManagedUserSaveResult,
} from '../models/managed-user.model';
import { ManagedUserDraft } from '../models/managed-user-draft.model';
import { MultiBuyerEntity } from '../models/multi-buyer-entity.model';
import { ProjectManager } from '../models/project-manager.model';
import {
  UserCompanyDetail,
  UserCompanySummary,
} from '../models/user-company.model';
import { nextManagedUserStatus } from '../utils/user-status.helper';
import { selectableCompanies } from '../utils/user-company.helper';

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly _userApi = inject(UserApi);
  private readonly _companyApi = inject(UserCompanyApi);
  private readonly _multiBuyerEntityApi = inject(UserMultiBuyerEntityApi);
  private readonly _projectManagerApi = inject(UserProjectManagerApi);

  public users(
    page: number,
    limit: number,
    search: string,
  ): Observable<ManagedUsersPage> {
    return this._userApi.users(page, limit, search);
  }

  public saveUser(draft: ManagedUserDraft): Observable<ManagedUserSaveResult> {
    const userId = draft.id;

    return userId
      ? this._userApi.update(userId, draft)
      : this._userApi.save(draft);
  }

  public toggleStatus(user: ManagedUser): Observable<string> {
    return this._userApi.updateStatus(
      user.id,
      user.name,
      nextManagedUserStatus(user.status),
    );
  }

  /** The token endpoint is only ever asked for API users. */
  public apiToken(userId: number): Observable<string> {
    return this._userApi.token(userId, true);
  }

  public companies(
    canManageMultiBuyerUsers: boolean,
  ): Observable<UserCompanySummary[]> {
    return this._companyApi
      .companies()
      .pipe(
        map((companies) =>
          selectableCompanies(companies, canManageMultiBuyerUsers),
        ),
      );
  }

  public companyDetail(companyId: number): Observable<UserCompanyDetail> {
    return this._companyApi.companyDetail(companyId);
  }

  public multiBuyerEntities(companyId: number): Observable<MultiBuyerEntity[]> {
    return this._multiBuyerEntityApi.entities(companyId);
  }

  public projectManagers(companyId: number): Observable<ProjectManager[]> {
    return this._projectManagerApi.projectManagers(companyId);
  }
}
